import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDitaContent,
  selectDitaContent,
} from "../../../components/Content/ContentSlice";
import classNames from "classnames";
import PropTypes from "prop-types";
import styles from "./GuidelineUpdatesPage.module.scss";
import {
  extractTitleFromDita,
  extractConbodyFromDita,
  hasHiddenHeadingMetadata,
} from "./guidelineUpdatesUtils";
import useAppModernisation from "../../../hooks/useAppModernisation";

// Container component
const GuidelineDateSection = ({ dateHref, dateTitle, updates }) => {
  const content = useSelector(selectDitaContent);
  const dispatch = useDispatch();

  const dateContent = getDitaContent(`Updates/${dateHref}`, content, dispatch);
  const resolvedDateTitle =
    extractTitleFromDita(dateContent) || dateTitle || "";

  return (
    <GuidelineDateSectionPresenter
      dateTitle={resolvedDateTitle}
      updates={updates}
    />
  );
};

GuidelineDateSection.propTypes = {
  dateHref: PropTypes.string.isRequired,
  dateTitle: PropTypes.string,
  updates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// Presenter component
const GuidelineDateSectionPresenter = ({ dateTitle, updates }) => {
  const [expanded, setExpanded] = useState(true);
  const { isMobileView } = useAppModernisation();

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div className={styles.dateSection}>
      {/* Date heading with expand/collapse */}
      <div className={styles.dateHeader} onClick={toggleExpanded}>
        <div
          className={classNames(
            isMobileView ? "size-18" : "size-20",
            "semibold",
            styles.dateTitle,
          )}
        >
          {dateTitle}
        </div>
        <i
          className={classNames(
            expanded ? "tg-icons-ChevronUp" : "tg-icons-ChevronDown",
            styles.dateIcon,
          )}
        ></i>
      </div>

      {/* Updates list - only shown when expanded */}
      {expanded && (
        <div className={styles.updatesContainer}>
          {updates.map((update, index) => (
            <GuidelineUpdateItemContainer key={index} update={update} />
          ))}
        </div>
      )}
    </div>
  );
};

GuidelineDateSectionPresenter.propTypes = {
  dateTitle: PropTypes.string.isRequired,
  updates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// Container component
const GuidelineUpdateItemContainer = ({ update }) => {
  const content = useSelector(selectDitaContent);
  const dispatch = useDispatch();

  const updateContent = getDitaContent(
    "Updates/" + update.attributes.href.value,
    content,
    dispatch,
  );

  const title = extractTitleFromDita(updateContent);
  const updateText = extractConbodyFromDita(updateContent);
  const hideHeading = hasHiddenHeadingMetadata(updateContent);
  const fallbackTitle =
    update.getAttribute("navtitle") ||
    update.getElementsByTagName("navtitle")[0]?.textContent ||
    "";

  return (
    <GuidelineUpdateItem
      title={title}
      fallbackTitle={fallbackTitle}
      updateText={updateText}
      hideHeading={hideHeading}
    />
  );
};

GuidelineUpdateItemContainer.propTypes = {
  update: PropTypes.shape({
    attributes: PropTypes.shape({
      href: PropTypes.shape({
        value: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

// Presenter component
const GuidelineUpdateItem = ({
  title,
  fallbackTitle,
  updateText,
  hideHeading,
}) => {
  const { isMobileView } = useAppModernisation();
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);
  const displayTitle = title || fallbackTitle;

  // Check if text is actually truncated
  useEffect(() => {
    if (textRef.current && updateText && !expanded) {
      const element = textRef.current;

      const checkTruncation = () => {
        if (element) {
          const isOverflowing = element.scrollHeight > element.clientHeight;
          setIsTruncated(isOverflowing);
        }
      };

      checkTruncation();

      // Also check after a short delay to ensure CSS is fully applied
      const timer = setTimeout(checkTruncation, 100);

      // Create ResizeObserver to watch for size changes (zoom, window resize, etc.)
      const resizeObserver = new ResizeObserver(() => {
        checkTruncation();
      });

      resizeObserver.observe(element);

      return () => {
        clearTimeout(timer);
        resizeObserver.disconnect();
      };
    }
  }, [updateText, expanded]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div className={styles.updateItem}>
      <div className={styles.dotIcon}></div>
      <div
        className={classNames(styles.updateContent, {
          [styles.noHeading]: hideHeading,
        })}
      >
        {!hideHeading && displayTitle && (
          <div
            className={classNames(
              isMobileView ? "size-16" : "size-18",
              "medium",
            )}
          >
            {displayTitle}
          </div>
        )}
        <div
          ref={textRef}
          className={classNames(
            isMobileView ? "size-14" : "size-16",
            expanded ? "" : styles.updateText,
          )}
          dangerouslySetInnerHTML={{ __html: updateText }}
        />
        {isTruncated && (
          <div
            className={classNames("size-14 medium", styles.readMoreLink)}
            onClick={toggleExpanded}
          >
            <div>{expanded ? "Show less" : "Show more"}</div>
            <i
              className={classNames(
                expanded ? "tg-icons-ChevronUp" : "tg-icons-ChevronDown",
                styles.readMoreIcon,
              )}
            ></i>
          </div>
        )}
      </div>
    </div>
  );
};

GuidelineUpdateItem.propTypes = {
  title: PropTypes.string.isRequired,
  fallbackTitle: PropTypes.string.isRequired,
  updateText: PropTypes.string.isRequired,
  hideHeading: PropTypes.bool.isRequired,
};

export default GuidelineDateSection;
